// meeting/meeting-form/meeting-form.component.ts
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { AbstractControl, FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { MeetingApiService } from '../services/meeting-api.service';
import { MeetingTypeApiService } from '../services/meeting-type-api.service';
import { MeetingStatusApiService } from '../services/meeting-status-api.service';
import { MeetingBooksLinkApiService } from '../services/meeting-books-link-api.service';
import {
  MeetingRequest,
  MeetingResponse,
  MeetingTypeResponse,
  MeetingStatusResponse,
  MeetingBooksLinkResponse,
  MeetingParticipantResponse,
} from '../models/meeting.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { CompanySummary, Contact } from '../../contactos/models/contacto.model';
import { CompanyResponse, ContactCompanyResponse } from '../../empresas/models/empresa.model';
import { catchError, debounceTime, distinctUntilChanged, map, of, Observable, shareReplay, Subject, switchMap, takeUntil } from 'rxjs';
import { SwitchComponent } from "../../../shared/components/form/input/switch.component";
import { EntitySelectComponent, EntitySelectPage } from '../../../shared/components/entity-select/entity-select.component';
import { UserApiService } from '../../users/services/user-api.service';
import { CrmUserResponse } from '../../users/models/user.model';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { Deal, DealContactResponse } from '../../deals/models/deal.model';
import { DealApiService } from '../../deals/services/deal-api.service';
import { DealContactApiService } from '../../deals/services/deal-contact-api.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  ProfileApiService,
  MailAccountResponse,
  MailProvider,
} from '../../profile/services/profile-api.service';

interface PendingCompany {
  company: CompanySummary;
  position: string;
  isPrimary: boolean;
  associationId?: number;
}

/** INC 39 — campos de "Relaciones" que participan en el autocompletado cruzado. */
type RelationField = 'contact' | 'company' | 'deal';

/** INC 39 — un candidato para rellenar Contacto / Empresa / Negocio. */
interface RelationOption {
  id: number;
  /** Texto ya listo para mostrar (nunca vacío; cae a "Negocio #12" si no hay nombre). */
  label: string;
  /** Segunda línea opcional: correo, cargo, rol, monto… */
  sublabel?: string;
  /** Marcado como principal en la tabla de asociación. */
  isPrimary?: boolean;
  /** true cuando `label` es un placeholder con id y hay que resolver el nombre real. */
  needsResolve?: boolean;
}

/** INC 39 — elección pendiente que se muestra al usuario cuando hay más de un candidato. */
interface RelationChoice {
  field: RelationField;
  title: string;
  hint: string;
  options: RelationOption[];
}

@Component({
  selector: 'app-meeting-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    EntitySelectComponent
  ],
  templateUrl: './meeting-form.component.html'
})
export class MeetingFormComponent implements OnInit, OnDestroy {
  @Input() drawerMode = false;
  @Input() prefilledContactId?: number;
  @Input() prefilledContactName?: string;
  @Input() prefilledCompanyId?: number;
  @Input() prefilledCompanyName?: string;
  @Input() prefilledDealId?: number;
  @Input() prefilledDealName?: string;
  @Input() prefilledDatetimeStart?: string;
  @Input() prefilledDatetimeEnd?: string;

  @Output() saved = new EventEmitter<MeetingResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  meetingId: number | null = null;
  currentMeeting: MeetingResponse | null = null;
  showAdvanced = false;

  // Responsable del usuario logeado
  currentUser: any = null;
  ownerDisplayName = '';

  // Catálogos
  meetingTypes: MeetingTypeResponse[] = [];
  loadingTypes = false;

  meetingStatuses: MeetingStatusResponse[] = [];
  loadingStatuses = false;

  // INC 26 — booking pages para prefill (solo en creación)
  bookingLinks: MeetingBooksLinkResponse[] = [];
  loadingBookingLinks = false;
  selectedBookingLinkId: number | null = null;

  // Cuentas OAuth de correo conectadas — para auto-link Meet/Teams
  mailAccounts: MailAccountResponse[] = [];
  loadingMailAccounts = false;

  // Zonas horarias IANA válidas (INC 16) — el backend rechaza valores fuera de esta lista
  readonly timezones: string[] = Intl.supportedValuesOf('timeZone');

  // Zona horaria detectada del navegador (para precargar y mostrar como sugerencia en creación)
  readonly detectedTimezone: string = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      return '';
    }
  })();
  // True cuando el valor del control `timezone` proviene de la autodetección (para mostrar el hint)
  timezoneAutoDetected = false;

  // Task 3 — opciones del picker visual de proveedor de video.
  // `value` debe coincidir EXACTAMENTE con lo que espera el backend / la lógica
  // (jitsi, google, microsoft, zoom, webex, other) o null para "sin video".
  // `auto` marca los providers donde el back genera el link automáticamente.
  readonly videoProviderOptions: {
    value: string | null;
    label: string;
    auto: boolean;
  }[] = [
    { value: null, label: 'Sin video / presencial', auto: false },
    { value: 'jitsi', label: 'Jitsi', auto: true },
    { value: 'google', label: 'Google Meet', auto: true },
    { value: 'microsoft', label: 'Microsoft Teams', auto: true },
    { value: 'zoom', label: 'Zoom', auto: false },
    { value: 'webex', label: 'Webex', auto: false },
    { value: 'other', label: 'Otro', auto: false },
  ];

  // Presets de duración (minutos)
  durationPresets: { value: number; label: string }[] = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 h' },
    { value: 90, label: '1 h 30' },
    { value: 120, label: '2 h' },
    { value: 180, label: '3 h' },
    { value: 240, label: '4 h' },
  ];

  // Modal post-guardado: link generado por el back + avisos persistentes
  // (enlace de video no generado, correo de confirmación no enviado).
  linkInfoVisible = false;
  linkInfoUrl: string | null = null;
  linkInfoProvider: string | null = null;
  linkCopied = false;
  /**
   * Avisos que deben quedar VISIBLES hasta que el usuario los cierre (no toasts):
   * fallo del enlace de video y/o correo de confirmación omitido.
   */
  postSaveWarnings: { title: string; message: string }[] = [];
  private postSaveMeeting: MeetingResponse | null = null;

  /**
   * True cuando el usuario ya intentó guardar al menos una vez: activa la
   * visualización del error de modalidad (presencial vs virtual).
   */
  submitAttempted = false;

  /** True si el banner superior (`error`) contiene el aviso de modalidad. */
  private modalidadErrorShown = false;

  // --- Contact typeahead ---
  name = '';
  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  showContactDropdown = false;
  searchingContacts = false;
  loadingMoreContacts = false;
  contactSearchPage = 0;
  contactSearchTotalPages = 0;
  idContact: number | null = null;

  // --- Deal typeahead ---
  dealSearchTerm = '';
  dealSearchResults: any[] = [];
  showDealDropdown = false;
  searchingDeals = false;
  loadingMoreDeals = false;
  dealSearchPage = 0;
  dealSearchTotalPages = 0;
  idDeal: number | null = null;

  // --- Company typeahead ---
  companySearchTerm = '';
  companySearchResults: CompanyResponse[] = [];
  showCompanyDropdown = false;
  searchingCompanies = false;
  loadingMoreCompanies = false;
  companySearchPage = 0;
  companySearchTotalPages = 0;
  idCompany: number | null = null;
  companyAutoFilled = false;
  /** Realce visual "Auto-completado" del campo Contacto (empresa→contacto / negocio→contacto). */
  contactAutoFilled = false;
  /** Ídem para Negocio (contacto→negocio / empresa→negocio). */
  dealAutoFilled = false;

  // INC 39 — elección de relación cuando hay MÁS DE UN candidato (un contacto con
  // varias empresas/negocios, una empresa con varios negocios, un negocio con
  // varios contactos). Antes se tomaba el "principal" o el más reciente en
  // silencio; ahora se le pregunta al usuario. `relationChoice` es el diálogo
  // visible y la cola guarda los que vienen detrás (p. ej. empresa Y negocio).
  relationChoice: RelationChoice | null = null;
  private relationChoiceQueue: RelationChoice[] = [];

  // INC 39 — qué campos fueron rellenados por el autocompletado (y no elegidos a
  // mano por el usuario ni precargados). Solo esos se pueden pisar/limpiar en una
  // cascada posterior: una elección explícita del usuario nunca se sobrescribe.
  private readonly autoFilledRelations = new Set<RelationField>();

  // INC 41 — participantes internos (usuarios del sistema). Multi-select vía
  // EntitySelect (buscar) + botón agregar + chips. NO incluye al organizador.
  internalParticipants: { userId: number; name: string }[] = [];
  newInternalUserId: number | null = null;
  newInternalUserName = '';
  /** Caché de usuarios del negocio para resolver nombres client-side. */
  private cachedUsers$: Observable<CrmUserResponse[]> | null = null;

  // subjects
  private readonly dealSearch$ = new Subject<string>();
  private readonly contactSearch$ = new Subject<string>();
  private readonly companySearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Auto-fill name (si lo necesitas)
  private nameAutoGenerated = false;
  nameAutoFilled = false;

  constructor(
    private fb: FormBuilder,
    private meetingApi: MeetingApiService,
    private meetingTypeApi: MeetingTypeApiService,
    private meetingStatusApi: MeetingStatusApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private empresaApi: EmpresaApiService,
    private contactoApi: ContactoApiService,
    private contactCompanyApi: ContactCompanyApiService,
    private dealApi: DealApiService,
    private dealContactApi: DealContactApiService,
    private authApiService: AuthApiService,
    private toast: ToastService,
    private profileApi: ProfileApiService,
    private booksLinkApi: MeetingBooksLinkApiService,
    private userApi: UserApiService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Configurar búsquedas
    this.setupContactSearch();
    this.setupCompanySearch();
    this.setupDealSearch();

    // INC 15 — limpiar el error `backend` de un control en cuanto el usuario lo edita
    this.setupBackendErrorClearing();

    // Obtener responsable actual
    this.currentUser = this.authService.getCurrentUser();
    this.ownerDisplayName = this.currentUser ? `${this.currentUser.fullname} ${this.currentUser.lastname || ''}`.trim() : '';

    if (this.currentUser?.id) {
      this.form.patchValue({ organizerId: this.currentUser.id });
    }

    // Cargar catálogos
    this.cargarCatalogos();

    if (this.drawerMode) {
      // En modo drawer el wrapper invoca reset()/loadMeeting() al abrir.
      this.applyPrefills();
      this.applyDetectedTimezone();
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.meetingId = +id;
      this.loadMeeting(this.meetingId);
    } else {
      // Creación (página): precargar zona horaria detectada del navegador.
      this.applyDetectedTimezone();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Setup typeahead pipes ───────────────────────────────────────────────

  private setupContactSearch(): void {
    this.contactSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        this.contactSearchPage = 0;
        this.contactSearchResults = [];
        this.contactSearchTotalPages = 0;

        if (term.length === 0) {
          this.showContactDropdown = false;
          return of(null);
        }

        this.searchingContacts = true;
        const businessId = this.authService.getCurrentUser()?.businessId || 1;
        return this.contactoApi.searchContacts({ businessId, searchTerm: term, page: 0, size: 10 })
          .pipe(catchError(() => of(null)));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.searchingContacts = false;
      if (response) {
        this.contactSearchResults = response.content;
        this.contactSearchTotalPages = response.totalPages;
        this.showContactDropdown = this.contactSearchResults.length > 0;
      }
    });
  }

  private setupCompanySearch(): void {
    this.companySearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        this.companySearchPage = 0;
        this.companySearchResults = [];
        this.companySearchTotalPages = 0;

        if (term.length === 0) {
          this.showCompanyDropdown = false;
          return of(null);
        }

        this.searchingCompanies = true;
        const businessId = this.authService.getCurrentUser()?.businessId || 1;
        return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page: 0, size: 10 })
          .pipe(catchError(() => of(null)));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.searchingCompanies = false;
      if (response) {
        this.companySearchResults = response.content;
        this.companySearchTotalPages = response.totalPages;
        this.showCompanyDropdown = this.companySearchResults.length > 0;
      }
    });
  }

  private setupDealSearch(): void {
    this.dealSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        this.dealSearchPage = 0;
        this.dealSearchResults = [];
        this.dealSearchTotalPages = 0;

        if (term.length === 0) {
          this.showDealDropdown = false;
          return of(null);
        }

        this.searchingDeals = true;
        const businessId = this.authService.getCurrentUser()?.businessId || 1;
        return this.dealApi.searchDeals({ businessId, searchName: term, page: 0, size: 10 })
          .pipe(catchError(() => of(null)));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.searchingDeals = false;
      if (response) {
        this.dealSearchResults = response.content;
        this.dealSearchTotalPages = response.totalPages;
        this.showDealDropdown = this.dealSearchResults.length > 0;
      }
    });
  }

  // ─── Contact typeahead methods ───────────────────────────────────────────

  onContactSearchInput(term: string): void {
    this.contactSearchTerm = term;
    this.idContact = null;
    this.contactAutoFilled = false;
    this.autoFilledRelations.delete('contact');
    this.form.patchValue({ contactId: null });
    this.contactSearch$.next(term);
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactSearchPage = 0;
    this.contactSearchResults = [];
    this.contactSearchTotalPages = 0;
    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactoApi.searchContacts({
      businessId,
      searchTerm: this.contactSearchTerm.trim(),
      page: 0,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onContactDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreContacts) return;
    if (this.contactSearchPage + 1 >= this.contactSearchTotalPages) return;

    this.loadingMoreContacts = true;
    this.contactSearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactoApi.searchContacts({
      businessId,
      searchTerm: this.contactSearchTerm.trim(),
      page: this.contactSearchPage,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.loadingMoreContacts = false;
        if (response) {
          this.contactSearchResults = [...this.contactSearchResults, ...response.content];
          this.contactSearchTotalPages = response.totalPages;
        }
      });
  }

  selectContact(contact: Contact): void {
    this.idContact = contact.id!;
    this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
    this.showContactDropdown = false;
    this.contactSearchResults = [];
    this.form.patchValue({ contactId: contact.id });
    // Elección explícita del usuario: deja de ser un valor auto-rellenado.
    this.autoFilledRelations.delete('contact');

    // INC 39 — al elegir un CONTACTO, autocompletar Empresa y Negocio. Si el
    // contacto tiene más de uno, se pregunta cuál. Los campos quedan editables.
    if (contact.id) {
      this.autofillFromContact(contact.id);
    }
  }

  /**
   * INC 39 — a partir del contacto obtiene TODAS sus empresas y TODOS sus negocios.
   * Con un solo candidato se rellena directo; con varios se abre el selector (antes
   * se tomaba el "principal" o el último creado en silencio). Muestra SIEMPRE el
   * nombre, nunca el id.
   */
  private autofillFromContact(contactId: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    // Empresas del contacto (tabla de asociación contact_company).
    this.contactCompanyApi.getByContact(contactId, businessId).pipe(
      catchError(() => of([] as ContactCompanyResponse[])),
      takeUntil(this.destroy$)
    ).subscribe(assocs => {
      const options = this.sortRelationOptions(
        (assocs || []).map(a => this.toRelationOption(
          a.idCompany,
          a.company?.commercialName,
          'Empresa',
          a.position || undefined,
          a.isPrimary
        ))
      );
      this.resolveRelation('company', options, {
        title: 'Elige la empresa',
        hint: 'Este contacto está vinculado a varias empresas. Selecciona la que corresponde a esta reunión.',
      });
    });

    // Negocios del contacto (tabla de asociación deal_contact).
    this.dealContactApi.getByContact(contactId, businessId).pipe(
      catchError(() => of([] as DealContactResponse[])),
      takeUntil(this.destroy$)
    ).subscribe(links => {
      const options = this.sortRelationOptions(
        (links || []).map(l => this.toRelationOption(
          l.idDeal,
          l.dealName,
          'Negocio',
          l.roleName || undefined,
          l.isPrimary
        ))
      );
      this.resolveRelation('deal', options, {
        title: 'Elige el negocio',
        hint: 'Este contacto participa en varios negocios. Selecciona el que corresponde a esta reunión.',
      });
    });
  }

  closeContactDropdown(): void {
    setTimeout(() => { this.showContactDropdown = false; }, 200);
  }

  clearContact(): void {
    this.idContact = null;
    this.contactSearchTerm = '';
    this.contactAutoFilled = false;
    this.autoFilledRelations.delete('contact');
    this.form.patchValue({ contactId: null });
    this.contactSearchResults = [];
    this.showContactDropdown = false;
    
    setTimeout(() => {
      const input = document.querySelector('input[placeholder="Buscar contacto..."]') as HTMLInputElement;
      input?.focus();
    }, 0);
  }

  // ─── Deal typeahead methods ───────────────────────────────────────────

  onDealSearchInput(term: string): void {
    this.dealSearchTerm = term;
    this.idDeal = null;
    this.autoFilledRelations.delete('deal');
    this.form.patchValue({ dealId: null });
    this.dealSearch$.next(term);
  }

  onDealSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.dealSearchPage = 0;
    this.dealSearchResults = [];
    this.dealSearchTotalPages = 0;
    this.searchingDeals = true;
    this.showDealDropdown = false;

    this.dealApi.searchDeals({
      businessId,
      searchName: this.dealSearchTerm.trim(),
      page: 0,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.searchingDeals = false;
        if (response) {
          this.dealSearchResults = response.content;
          this.dealSearchTotalPages = response.totalPages;
          this.showDealDropdown = this.dealSearchResults.length > 0;
        }
      });
  }

  onDealDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreDeals) return;
    if (this.dealSearchPage + 1 >= this.dealSearchTotalPages) return;

    this.loadingMoreDeals = true;
    this.dealSearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.dealApi.searchDeals({
      businessId,
      searchName: this.dealSearchTerm.trim(),
      page: this.dealSearchPage,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.loadingMoreDeals = false;
        if (response) {
          this.dealSearchResults = [...this.dealSearchResults, ...response.content];
          this.dealSearchTotalPages = response.totalPages;
        }
      });
  }

  selectDeal(deal: any): void {
    this.idDeal = deal.id;
    this.dealSearchTerm = deal.name || deal.title || `Negocio #${deal.id}`;
    this.showDealDropdown = false;
    this.dealSearchResults = [];
    this.form.patchValue({ dealId: deal.id });
    // Elección explícita del usuario: deja de ser un valor auto-rellenado.
    this.autoFilledRelations.delete('deal');
    // INC 39 — al elegir un negocio, autocompletar Contacto y Empresa.
    this.autofillFromDeal(deal.id);
  }

  /**
   * INC 39 — autocompleta Contacto y Empresa a partir del negocio.
   * - Empresa: un negocio pertenece a UNA sola empresa (`deal.idCompany`) → directo.
   * - Contacto: se piden TODOS los contactos del negocio (deal_contact); con uno se
   *   rellena directo y con varios se pregunta cuál. Si esa consulta no devuelve
   *   nada se cae al `primaryContactId` denormalizado del detalle.
   * Si el negocio no tiene contacto/empresa, el campo queda vacío (sin error).
   */
  private autofillFromDeal(dealId: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    const deal$ = this.dealApi
      .getDealById(dealId, businessId)
      .pipe(catchError(() => of(null)), shareReplay(1));

    // Empresa del negocio (siempre a lo sumo una).
    deal$.pipe(takeUntil(this.destroy$)).subscribe(deal => {
      if (!deal?.idCompany) return;
      this.resolveRelation(
        'company',
        [this.toRelationOption(deal.idCompany, deal.companyName, 'Empresa')],
        { title: 'Elige la empresa', hint: '' }
      );
    });

    // Contactos del negocio.
    this.dealContactApi.getByDeal(dealId, businessId).pipe(
      catchError(() => of([] as DealContactResponse[])),
      switchMap(links => {
        if (links?.length) {
          return of(links.map(l => this.toRelationOption(
            l.idContact,
            `${l.contactFullname ?? ''} ${l.contactLastname ?? ''}`.trim(),
            'Contacto',
            l.contactMail || l.roleName || undefined,
            l.isPrimary
          )));
        }
        // Respaldo: el detalle del negocio trae el contacto principal denormalizado.
        return deal$.pipe(map(deal => deal?.primaryContactId
          ? [this.toRelationOption(deal.primaryContactId, deal.primaryContactName, 'Contacto')]
          : []));
      }),
      takeUntil(this.destroy$)
    ).subscribe(options => {
      this.resolveRelation('contact', this.sortRelationOptions(options), {
        title: 'Elige el contacto',
        hint: 'Este negocio tiene varios contactos asociados. Selecciona a quién va dirigida la reunión.',
      });
    });
  }

  closeDealDropdown(): void {
    setTimeout(() => { this.showDealDropdown = false; }, 200);
  }

  clearDeal(): void {
    this.idDeal = null;
    this.dealSearchTerm = '';
    this.dealAutoFilled = false;
    this.autoFilledRelations.delete('deal');
    this.form.patchValue({ dealId: null });
    this.dealSearchResults = [];
    this.showDealDropdown = false;
    
    setTimeout(() => {
      const input = document.querySelector('input[placeholder="Buscar negocio..."]') as HTMLInputElement;
      input?.focus();
    }, 0);
  }
  
  // ─── Company typeahead methods ────────────────────────────────────────────
  
  onCompanySearchInput(term: string): void {
    this.companySearchTerm = term;
    this.idCompany = null;
    this.autoFilledRelations.delete('company');
    this.form.patchValue({ companyId: null });
    this.companySearch$.next(term);
  }
  
  onCompanySearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.companySearchPage = 0;
    this.companySearchResults = [];
    this.companySearchTotalPages = 0;
    this.searchingCompanies = true;
    this.showCompanyDropdown = false;

    this.empresaApi.searchCompanies({
      businessId,
      searchTerm: this.companySearchTerm.trim(),
      page: 0,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.searchingCompanies = false;
        if (response) {
          this.companySearchResults = response.content;
          this.companySearchTotalPages = response.totalPages;
          this.showCompanyDropdown = this.companySearchResults.length > 0;
        }
      });
  }
  
  onCompanyDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreCompanies) return;
    if (this.companySearchPage + 1 >= this.companySearchTotalPages) return;

    this.loadingMoreCompanies = true;
    this.companySearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.empresaApi.searchCompanies({
      businessId,
      searchTerm: this.companySearchTerm.trim(),
      page: this.companySearchPage,
      size: 10
    }).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(response => {
        this.loadingMoreCompanies = false;
        if (response) {
          this.companySearchResults = [...this.companySearchResults, ...response.content];
          this.companySearchTotalPages = response.totalPages;
        }
      });
  }
  
  selectCompany(company: CompanyResponse): void {
    this.idCompany = company.id;
    this.companySearchTerm = company.commercialName;
    this.showCompanyDropdown = false;
    this.companySearchResults = [];
    this.form.patchValue({ companyId: company.id });
    // Elección explícita del usuario: deja de ser un valor auto-rellenado.
    this.autoFilledRelations.delete('company');

    // INC 39 — al elegir una EMPRESA, autocompletar Contacto y Negocio. Con más de
    // un candidato se pregunta cuál. Los campos quedan editables por el usuario.
    if (company.id) {
      this.autofillFromCompany(company.id, company);
    }
  }

  /**
   * INC 39 — autocompleta Contacto y Negocio a partir de la empresa. Muestra SIEMPRE
   * el nombre, nunca el id.
   *
   * Contactos: tabla de asociación `contact_company` (todos los de la empresa). NO se
   * usa `company.contactId`: es legado (columna `company.int_id_contact`) y siempre
   * llega null, que era la causa de que el contacto nunca se rellenara. Si la
   * asociación no devuelve nada se cae a `primaryContactId` del listado/detalle.
   *
   * Negocios: ya no se toma "el más reciente" a ciegas — se piden todos los de la
   * empresa y si hay varios decide el usuario.
   */
  private autofillFromCompany(companyId: number, listItem?: CompanyResponse): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    /** Respaldo: contacto principal denormalizado del listado o del detalle. */
    const primaryContactFallback = (): Observable<RelationOption[]> => {
      if (listItem?.primaryContactId) {
        return of([this.toRelationOption(listItem.primaryContactId, listItem.primaryContactName, 'Contacto')]);
      }
      // El objeto de la lista no lo trae (p. ej. empresa venida de un create/update,
      // que el backend no enriquece) → pedir el detalle.
      return this.empresaApi.getCompanyById(companyId).pipe(
        catchError(() => of(null)),
        map(company => company?.primaryContactId
          ? [this.toRelationOption(company.primaryContactId, company.primaryContactName, 'Contacto')]
          : [])
      );
    };

    this.contactCompanyApi.getByCompany(companyId, businessId).pipe(
      catchError(() => of([] as ContactCompanyResponse[])),
      switchMap(assocs => {
        if (!assocs?.length) return primaryContactFallback();
        return of(assocs.map(a => this.toRelationOption(
          a.idContact,
          `${a.contact?.fullname ?? ''} ${a.contact?.lastname ?? ''}`.trim(),
          'Contacto',
          a.contact?.mail || a.position || undefined,
          a.isPrimary
        )));
      }),
      takeUntil(this.destroy$)
    ).subscribe(options => {
      this.resolveRelation('contact', this.sortRelationOptions(options), {
        title: 'Elige el contacto',
        hint: 'Esta empresa tiene varios contactos. Selecciona a quién va dirigida la reunión.',
      });
    });

    // Negocios de la empresa (los más recientes primero, sin archivados).
    this.dealApi.searchDeals({
      businessId,
      idCompany: companyId,
      size: 50,
      sortBy: 'datetimeCreated',
      sortDirection: 'DESC',
    }).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      const options = (res?.content || [])
        .filter(deal => !!deal.id && !deal.archived)
        .map(deal => this.toRelationOption(deal.id!, deal.name, 'Negocio', deal.dealCycleName || undefined));
      this.resolveRelation('deal', options, {
        title: 'Elige el negocio',
        hint: 'Esta empresa tiene varios negocios abiertos. Selecciona el que corresponde a esta reunión.',
      });
    });
  }

  // ─── INC 39: resolución de relaciones (1 candidato → directo, N → preguntar) ──

  /** Construye una opción con etiqueta lista para mostrar (nunca vacía). */
  private toRelationOption(
    id: number,
    name: string | null | undefined,
    placeholderPrefix: string,
    sublabel?: string,
    isPrimary?: boolean
  ): RelationOption {
    const label = (name || '').trim();
    return {
      id,
      label: label || `${placeholderPrefix} #${id}`,
      sublabel: sublabel?.trim() || undefined,
      isPrimary: !!isPrimary,
      needsResolve: !label,
    };
  }

  /** Principal primero; el resto conserva el orden que devolvió la API. */
  private sortRelationOptions(options: RelationOption[]): RelationOption[] {
    const seen = new Set<number>();
    const unique = options.filter(o => !!o.id && !seen.has(o.id) && seen.add(o.id));
    return [...unique].sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary));
  }

  private currentRelationId(field: RelationField): number | null {
    if (field === 'contact') return this.idContact;
    if (field === 'company') return this.idCompany;
    return this.idDeal;
  }

  /**
   * Decide qué hacer con los candidatos de un campo:
   * - 0 candidatos → si el valor actual lo puso el autocompletado, se limpia (venía
   *   de otra selección y ya no aplica); si lo eligió el usuario, se respeta.
   * - 1 candidato  → se rellena directo.
   * - N candidatos → se abre el selector, salvo que el valor actual ya sea uno de
   *   ellos (coherente: no molestar) o lo haya elegido el usuario a mano.
   */
  private resolveRelation(
    field: RelationField,
    options: RelationOption[],
    labels: { title: string; hint: string }
  ): void {
    const currentId = this.currentRelationId(field);
    const isAuto = this.autoFilledRelations.has(field);

    if (!options.length) {
      if (currentId != null && isAuto) this.clearRelation(field);
      return;
    }
    // Un valor elegido por el usuario (o precargado desde otra pantalla) manda.
    if (currentId != null && !isAuto) return;
    if (currentId != null && options.some(o => o.id === currentId)) return;

    if (options.length === 1) {
      this.applyRelation(field, options[0]);
      return;
    }
    this.enqueueRelationChoice({ field, title: labels.title, hint: labels.hint, options });
  }

  /** Rellena el campo con la opción elegida y lo marca como auto-completado. */
  private applyRelation(field: RelationField, option: RelationOption): void {
    switch (field) {
      case 'contact':
        this.idContact = option.id;
        this.contactSearchTerm = option.label;
        this.form.patchValue({ contactId: option.id });
        this.flashContactAutoFill();
        if (option.needsResolve) this.resolveContactName(option.id);
        break;
      case 'company':
        this.idCompany = option.id;
        this.companySearchTerm = option.label;
        this.form.patchValue({ companyId: option.id });
        this.flashCompanyAutoFill();
        if (option.needsResolve) this.resolveCompanyName(option.id);
        break;
      case 'deal':
        this.idDeal = option.id;
        this.dealSearchTerm = option.label;
        this.form.patchValue({ dealId: option.id });
        this.flashDealAutoFill();
        if (option.needsResolve) this.resolveDealName(option.id);
        break;
    }
    this.autoFilledRelations.add(field);
  }

  /** Vacía un campo que había rellenado el autocompletado y ya no aplica. */
  private clearRelation(field: RelationField): void {
    switch (field) {
      case 'contact':
        this.idContact = null;
        this.contactSearchTerm = '';
        this.form.patchValue({ contactId: null });
        break;
      case 'company':
        this.idCompany = null;
        this.companySearchTerm = '';
        this.form.patchValue({ companyId: null });
        break;
      case 'deal':
        this.idDeal = null;
        this.dealSearchTerm = '';
        this.form.patchValue({ dealId: null });
        break;
    }
    this.autoFilledRelations.delete(field);
  }

  /** Encola una elección; si ya hay una del mismo campo pendiente, la reemplaza. */
  private enqueueRelationChoice(choice: RelationChoice): void {
    if (this.relationChoice?.field === choice.field) return;
    this.relationChoiceQueue = this.relationChoiceQueue.filter(c => c.field !== choice.field);
    this.relationChoiceQueue.push(choice);
    if (!this.relationChoice) this.showNextRelationChoice();
  }

  private showNextRelationChoice(): void {
    this.relationChoice = this.relationChoiceQueue.shift() || null;
  }

  /** El usuario eligió una de las opciones del selector. */
  chooseRelationOption(option: RelationOption): void {
    const field = this.relationChoice?.field;
    if (field) this.applyRelation(field, option);
    this.showNextRelationChoice();
  }

  /** El usuario prefirió no elegir: el campo queda vacío y editable. */
  skipRelationChoice(): void {
    this.showNextRelationChoice();
  }

  private clearRelationChoices(): void {
    this.relationChoice = null;
    this.relationChoiceQueue = [];
    this.autoFilledRelations.clear();
  }

  clearCompany(): void {
    this.idCompany = null;
    this.companySearchTerm = '';
    this.companyAutoFilled = false;
    this.autoFilledRelations.delete('company');
    this.form.patchValue({ companyId: null });
  }
  
  closeCompanyDropdown(): void {
    setTimeout(() => { this.showCompanyDropdown = false; }, 200);
  }
  
  private flashCompanyAutoFill(): void {
    this.companyAutoFilled = true;
    setTimeout(() => { this.companyAutoFilled = false; }, 2000);
  }

  /** Realce visual del campo Contacto cuando se rellenó solo (desde Empresa o Negocio). */
  private flashContactAutoFill(): void {
    this.contactAutoFilled = true;
    setTimeout(() => { this.contactAutoFilled = false; }, 2000);
  }

  /** Ídem para el campo Negocio (desde Contacto o Empresa). */
  private flashDealAutoFill(): void {
    this.dealAutoFilled = true;
    setTimeout(() => { this.dealAutoFilled = false; }, 2000);
  }

  /**
   * Último recurso: cuando el autocompletado solo pudo obtener el id del contacto,
   * resuelve su nombre real para no dejar visible un "Contacto #12".
   */
  private resolveContactName(contactId: number): void {
    this.contactoApi.getContactById(contactId).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(contact => {
      if (!contact || this.idContact !== contactId) return;
      const name = `${contact.fullname ?? ''} ${contact.lastname ?? ''}`.trim();
      if (name) this.contactSearchTerm = name;
    });
  }

  /** Ídem para la empresa: evita mostrar "Empresa #7" si no llegó el nombre. */
  private resolveCompanyName(companyId: number): void {
    this.empresaApi.getCompanyById(companyId).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(company => {
      if (!company || this.idCompany !== companyId) return;
      if (company.commercialName) this.companySearchTerm = company.commercialName;
    });
  }

  /** Ídem para el negocio: evita mostrar "Negocio #12" si no llegó el nombre. */
  private resolveDealName(dealId: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.dealApi.getDealById(dealId, businessId).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(deal => {
      if (!deal || this.idDeal !== dealId) return;
      if (deal.name) this.dealSearchTerm = deal.name;
    });
  }

  // ─── INC 41: participantes internos (usuarios del sistema) ──────────────────

  /** Carga (una sola vez) los usuarios activos del negocio para búsqueda/nombres. */
  private loadBusinessUsers(): Observable<CrmUserResponse[]> {
    if (!this.cachedUsers$) {
      const businessId = this.authService.getCurrentUser()?.businessId || 1;
      this.cachedUsers$ = this.userApi.listActiveByBusiness(businessId).pipe(
        catchError(() => of([] as CrmUserResponse[])),
        shareReplay(1)
      );
    }
    return this.cachedUsers$;
  }

  /** Búsqueda client-side de usuarios (el endpoint no filtra por texto). Excluye
   *  al organizador y a los ya agregados. */
  userSearch = (term: string): Observable<EntitySelectPage<CrmUserResponse>> => {
    return this.loadBusinessUsers().pipe(
      map((users) => {
        const t = term.toLowerCase();
        const organizerId = this.form.get('organizerId')?.value;
        const selectedIds = new Set(this.internalParticipants.map((p) => p.userId));
        const items = users.filter((u) => {
          if (u.id === organizerId) return false;
          if (selectedIds.has(u.id)) return false;
          if (!t) return true;
          return `${u.fullname} ${u.lastname ?? ''} ${u.mail}`.toLowerCase().includes(t);
        });
        return { items, totalPages: 1 };
      }),
      catchError(() => of({ items: [] as CrmUserResponse[], totalPages: 0 }))
    );
  };

  userLabel = (u: CrmUserResponse): string =>
    `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();
  userSublabel = (u: CrmUserResponse): string => u.mail;

  onInternalUserPicked(u: CrmUserResponse | null): void {
    this.newInternalUserId = u ? u.id : null;
    this.newInternalUserName = u ? this.userLabel(u) : '';
  }

  addInternalParticipant(): void {
    const id = this.newInternalUserId;
    if (!id) return;
    const organizerId = this.form.get('organizerId')?.value;
    if (id === organizerId) return; // el organizador nunca va en la lista
    if (this.internalParticipants.some((p) => p.userId === id)) return;
    this.internalParticipants = [
      ...this.internalParticipants,
      { userId: id, name: this.newInternalUserName || `Usuario #${id}` },
    ];
    this.newInternalUserId = null;
    this.newInternalUserName = '';
  }

  removeInternalParticipant(userId: number): void {
    this.internalParticipants = this.internalParticipants.filter((p) => p.userId !== userId);
  }

  /** En edición: precarga los participantes internos actuales de la reunión. */
  private loadInternalParticipants(meetingId: number): void {
    this.meetingApi
      .listParticipants(meetingId)
      .pipe(catchError(() => of([] as MeetingParticipantResponse[])), takeUntil(this.destroy$))
      .subscribe((parts) => {
        const internal = (parts || []).filter(
          (p) => p.participantType !== 'organizer' && p.userId != null
        );
        if (!internal.length) {
          this.internalParticipants = [];
          return;
        }
        this.loadBusinessUsers()
          .pipe(takeUntil(this.destroy$))
          .subscribe((users) => {
            const nameById = new Map(users.map((u) => [u.id, this.userLabel(u)]));
            this.internalParticipants = internal.map((p) => ({
              userId: p.userId!,
              name: p.name || nameById.get(p.userId!) || `Usuario #${p.userId}`,
            }));
          });
      });
  }

  // ─── Catalogues & meeting loading ────────────────────────────────────────────

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingTypes = true;
    this.meetingTypeApi.listActive(businessId).subscribe({
      next: (data) => {
        this.meetingTypes = data;
        this.loadingTypes = false;
      },
      error: () => (this.loadingTypes = false),
    });

    this.loadingStatuses = true;
    this.meetingStatusApi.listActive(businessId).subscribe({
      next: (data) => {
        this.meetingStatuses = data;
        this.loadingStatuses = false;
        // INC 38 — solo en creación: preseleccionar el estado con isDefault===true.
        // Si ninguno lo tiene, no se preselecciona (sin romper).
        if (!this.isEditing && !this.form.get('idMeetingStatus')?.value) {
          const def = data.find((s) => s.isDefault === true);
          if (def) this.form.patchValue({ idMeetingStatus: def.id });
        }
      },
      error: () => (this.loadingStatuses = false),
    });

    this.loadingMailAccounts = true;
    this.profileApi.getMailAccounts().subscribe({
      next: (accounts) => {
        this.mailAccounts = accounts || [];
        this.loadingMailAccounts = false;
      },
      error: () => {
        this.mailAccounts = [];
        this.loadingMailAccounts = false;
      },
    });

    // INC 26 — el selector de booking page solo aplica en creación
    if (!this.isEditing) {
      this.loadingBookingLinks = true;
      this.booksLinkApi.listByBusiness(businessId).subscribe({
        next: (links) => {
          this.bookingLinks = (links || []).filter((l) => l.isActive !== false);
          this.loadingBookingLinks = false;
        },
        error: () => {
          this.bookingLinks = [];
          this.loadingBookingLinks = false;
        },
      });
    }
  }

  // ─── INC 40: modalidad presencial (location) vs virtual (meetingUrl) ────────
  // Un solo campo de enlace (meetingUrl). Presencial y virtual son excluyentes:
  // el backend da 400 si location y meetingUrl vienen juntos.

  /**
   * Al elegir un canal de video la reunión pasa a ser virtual: se limpia la
   * ubicación presencial. Los providers automáticos (jitsi/google/microsoft)
   * generan el enlace en el backend, así que también se limpia meetingUrl manual.
   * "Sin video / presencial" (null) limpia el enlace para volver a presencial.
   */
  setVideoProvider(value: string | null): void {
    this.form.patchValue({ videoProvider: value });
    if (value) {
      this.form.patchValue({ location: '', locationMapUrl: '' });
      if (this.isAutoVideoProvider(value)) {
        this.form.patchValue({ meetingUrl: '' });
      }
    } else {
      this.form.patchValue({ meetingUrl: '' });
    }
  }

  // ─── Validación de modalidad ANTES de guardar (presencial vs virtual) ───────
  // El backend responde 400 si la reunión no es ni presencial ni virtual, y en
  // ese caso además NO envía el correo de confirmación al cliente. Se valida en
  // el formulario para que el usuario sepa qué le falta ANTES de darle a Guardar;
  // el 400 queda como segundo cinturón.

  /** True si el usuario indicó un lugar físico (reunión presencial). */
  get hasPresencial(): boolean {
    return !!(this.form?.get('location')?.value || '').toString().trim();
  }

  /**
   * True si la reunión es virtual: basta con haber elegido un proveedor de
   * videollamada (google/microsoft/jitsi generan el enlace en el backend DESPUÉS
   * de guardar) o haber pegado una URL manual. NUNCA exigir URL con providers
   * automáticos: rompería el flujo normal.
   */
  get hasVirtual(): boolean {
    const provider = this.form?.get('videoProvider')?.value;
    const url = (this.form?.get('meetingUrl')?.value || '').toString().trim();
    return !!provider || !!url;
  }

  /** Tipo de reunión seleccionado (para requiresLocation / requiresUrl). */
  private get selectedMeetingType(): MeetingTypeResponse | undefined {
    const id = +this.form?.get('idMeetingType')?.value;
    if (!id) return undefined;
    return this.meetingTypes.find((t) => t.id === id);
  }

  /**
   * Mensaje de error de modalidad, o '' si la reunión es válida. Se recalcula en
   * cada ciclo de detección de cambios, así que el aviso desaparece solo en
   * cuanto el usuario completa el lugar o elige un canal de video.
   * Los textos replican los del backend para que el usuario vea lo mismo antes
   * y después de guardar.
   */
  get modalidadError(): string {
    if (!this.form) return '';

    if (!this.hasPresencial && !this.hasVirtual) {
      return 'Indique el lugar de la reunión (presencial) o seleccione un proveedor de videollamada (virtual). Una reunión debe ser presencial o virtual.';
    }

    const type = this.selectedMeetingType;
    if (type?.requiresLocation && !this.hasPresencial) {
      return `El tipo de reunión "${type.name}" requiere indicar el lugar de la reunión (presencial).`;
    }
    if (type?.requiresUrl && !this.hasVirtual) {
      return `El tipo de reunión "${type.name}" requiere un enlace de videollamada: seleccione un proveedor o pegue el enlace.`;
    }
    return '';
  }

  /** True cuando hay que pintar el error de modalidad en el formulario. */
  get showModalidadError(): boolean {
    return this.submitAttempted && !!this.modalidadError;
  }

  /** True si la tarjeta `value` es el provider actualmente seleccionado. */
  isVideoProviderSelected(value: string | null): boolean {
    const current = this.form?.get('videoProvider')?.value ?? null;
    return current === value;
  }

  // ─── INC 26: selección de booking page con prefill ───────────────────────

  bookingLinkLabel(link: MeetingBooksLinkResponse): string {
    return link.title || link.slug || `Booking #${link.id}`;
  }

  onBookingLinkChange(): void {
    const id = this.selectedBookingLinkId ? +this.selectedBookingLinkId : null;
    this.selectedBookingLinkId = id;
    if (!id) return;

    const link = this.bookingLinks.find((l) => l.id === id);
    if (!link) return;

    // Precargar valores conocidos solo si el usuario aún no los llenó.
    const patch: Record<string, unknown> = {};
    if (link.defaultDurationMinutes && !this.userTouchedDuration()) {
      patch['durationMinutes'] = link.defaultDurationMinutes;
    }
    if (link.timezone && !this.form.get('timezone')?.value) {
      patch['timezone'] = link.timezone;
      this.showAdvanced = true;
    }
    if (link.idType && !this.form.get('idMeetingType')?.value) {
      patch['idMeetingType'] = link.idType;
    }
    if (link.videoProvider && !this.form.get('videoProvider')?.value) {
      patch['videoProvider'] = link.videoProvider;
      this.showAdvanced = true;
    }
    if (link.customVideoUrl && !this.form.get('meetingUrl')?.value) {
      patch['meetingUrl'] = link.customVideoUrl;
      // El enlace personalizado se trata como provider manual "Otro" (virtual).
      if (!this.form.get('videoProvider')?.value) {
        patch['videoProvider'] = 'other';
        patch['location'] = '';
        patch['locationMapUrl'] = '';
      }
    }
    if (Object.keys(patch).length) {
      this.form.patchValue(patch);
    }
  }

  /** Heurística: duración distinta del default inicial (60) = el usuario la tocó. */
  private userTouchedDuration(): boolean {
    return +this.form.get('durationMinutes')?.value !== 60;
  }

  // ─── Video provider helpers (Google Meet / Microsoft Teams auto-link) ───

  /** Devuelve true si el usuario tiene esa cuenta OAuth en estado CONNECTED. */
  isProviderConnected(provider: MailProvider): boolean {
    const acc = this.mailAccounts.find((a) => a.provider === provider);
    if (!acc) return false;
    const raw = (acc as any)?.connectionStatus ?? (acc as any)?.status ?? '';
    return typeof raw === 'string' && raw.toUpperCase() === 'CONNECTED';
  }

  /** True para los providers donde el back genera el link automáticamente. */
  isAutoVideoProvider(value: string | null | undefined): boolean {
    return value === 'google' || value === 'microsoft' || value === 'jitsi';
  }

  get isCurrentProviderAuto(): boolean {
    return this.isAutoVideoProvider(this.form?.get('videoProvider')?.value);
  }

  /** Warning si se eligió google/microsoft sin cuenta conectada. */
  get videoProviderWarning(): string {
    const v = this.form?.get('videoProvider')?.value;
    if (v === 'google' && !this.isProviderConnected('google')) {
      return 'No tienes Gmail conectado. Conéctalo en tu Perfil para generar links de Google Meet automáticamente.';
    }
    if (v === 'microsoft' && !this.isProviderConnected('microsoft')) {
      return 'No tienes Microsoft conectado. Conéctalo en tu Perfil para generar links de Teams automáticamente.';
    }
    return '';
  }

  loadMeeting(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.meetingId = id;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.meetingApi.getMeetingById(id, businessId).subscribe({
      next: (meeting: MeetingResponse) => {
        this.currentMeeting = meeting;
        
        // Formatear fechas para datetime-local
        const formatDateForInput = (dateStr: string) => {
          const date = new Date(dateStr);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        // Calcular duración en minutos desde start/end del meeting
        const startMs = new Date(meeting.datetimeStart).getTime();
        const endMs = meeting.datetimeEnd ? new Date(meeting.datetimeEnd).getTime() : startMs + 60 * 60000;
        const durationMinutes = Math.max(5, Math.round((endMs - startMs) / 60000));

        this.form.patchValue({
          title: meeting.title,
          description: meeting.description || '',
          datetimeStart: formatDateForInput(meeting.datetimeStart),
          durationMinutes,
          location: meeting.location || '',
          meetingUrl: meeting.meetingUrl || '',
          locationMapUrl: meeting.locationMapUrl || '',
          idMeetingType: meeting.idMeetingType || null,
          idMeetingStatus: meeting.idMeetingStatus || null,
          dealId: meeting.dealId || null,
          companyId: meeting.companyId || null,
          contactId: meeting.contactId || null,
          organizerId: meeting.organizerId || null,
          notes: meeting.notes || '',
          outcome: meeting.outcome || '',
          source: meeting.source || null,
          videoProvider: meeting.videoProvider || null,
          videoJoinUrl: meeting.videoJoinUrl || '',
          inviteeTimezone: meeting.inviteeTimezone || '',
          timezone: meeting.timezone || '',
          isNoShow: !!meeting.isNoShow,
          noShowDetectedAt: meeting.noShowDetectedAt
            ? formatDateForInput(meeting.noShowDetectedAt)
            : null,
          paymentStatus: meeting.paymentStatus || null,
          paymentAmount: meeting.paymentAmount ?? null,
          paymentIntentId: meeting.paymentIntentId || '',
          idCancellationReason: meeting.idCancellationReason || null,
          cancellationReasonText: meeting.cancellationReasonText || '',
          gdprConsentGiven: !!meeting.gdprConsentGiven,
          gdprConsentDatetime: meeting.gdprConsentDatetime
            ? formatDateForInput(meeting.gdprConsentDatetime)
            : null,
        });

        if (
          meeting.source ||
          meeting.paymentStatus ||
          meeting.isNoShow ||
          meeting.videoProvider ||
          meeting.idCancellationReason ||
          meeting.gdprConsentGiven
        ) {
          this.showAdvanced = true;
        }

        // Cargar el nombre del responsable para mostrar
      const creatorId = meeting.organizerId;
      const currentUserId = this.currentUser?.id;
      
        if (creatorId && currentUserId && creatorId !== currentUserId) {
          this.authApiService.getUserById(creatorId).subscribe({
            next: (u) => {
              this.ownerDisplayName = `${u.fullname} ${u.lastname || ''}`.trim();
            },
            error: () => {
              // El directorio resuelve también a los inactivos: fallar aquí es «ya no existe».
              this.ownerDisplayName = 'Usuario no disponible';
            }
          });
        } else {
          this.ownerDisplayName = this.currentUser ? `${this.currentUser.fullname} ${this.currentUser.lastname || ''}`.trim() : '';
        }

        
        // Cargar nombre del contacto
        if (meeting.contactId) {
          this.idContact = meeting.contactId;
          this.contactoApi.getContactById(meeting.contactId)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe(contact => {
              if (contact) {
                this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
              } else {
                this.contactSearchTerm = `Contacto #${meeting.contactId}`;
              }
            });
        }

        // Cargar nombre de la empresa
        if (meeting.companyId) {
          this.idCompany = meeting.companyId;
          this.empresaApi.getCompanyById(meeting.companyId)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe(company => {
              if (company) {
                this.companySearchTerm = company.commercialName;
              } else {
                this.companySearchTerm = `Empresa #${meeting.companyId}`;
              }
            });
        }

        // Cargar nombre del negocio (deal)
        if (meeting.dealId) {
          this.idDeal = meeting.dealId;
          this.dealApi.getDealById(meeting.dealId, businessId)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe(deal => {
              if (deal) {
                this.dealSearchTerm = deal.name || `Negocio #${meeting.dealId}`;
              } else {
                this.dealSearchTerm = `Negocio #${meeting.dealId}`;
              }
            });
        }
        
        // INC 41 — precargar participantes internos actuales de la reunión
        this.loadInternalParticipants(meeting.id);

        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la reunión';
        this.loading = false;
      }
    });
  }

  reset(): void {
    this.isEditing = false;
    this.meetingId = null;
    this.currentMeeting = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.showAdvanced = false;

    this.idContact = null;
    this.contactSearchTerm = '';
    this.contactSearchResults = [];
    this.showContactDropdown = false;
    this.contactAutoFilled = false;

    this.idCompany = null;
    this.companySearchTerm = '';
    this.companySearchResults = [];
    this.showCompanyDropdown = false;
    this.companyAutoFilled = false;

    this.idDeal = null;
    this.dealSearchTerm = '';
    this.dealSearchResults = [];
    this.showDealDropdown = false;
    this.dealAutoFilled = false;

    // INC 39 — descartar selectores de relación pendientes de la apertura anterior
    this.clearRelationChoices();

    // INC 41 — limpiar participantes internos
    this.internalParticipants = [];
    this.newInternalUserId = null;
    this.newInternalUserName = '';

    this.selectedBookingLinkId = null;
    this.timezoneAutoDetected = false;

    // Avisos / validación de modalidad del guardado anterior
    this.submitAttempted = false;
    this.modalidadErrorShown = false;
    this.linkInfoVisible = false;
    this.linkInfoUrl = null;
    this.linkInfoProvider = null;
    this.linkCopied = false;
    this.postSaveWarnings = [];

    this.initForm();
    if (this.currentUser?.id) {
      this.form.patchValue({ organizerId: this.currentUser.id });
      this.ownerDisplayName = `${this.currentUser.fullname} ${this.currentUser.lastname || ''}`.trim();
    }

    this.applyPrefills();
    this.applyDetectedTimezone();
  }

  private applyPrefills(): void {
    // INC 39 — quien abre el drawer no siempre tiene el nombre a mano (el listado de
    // negocios, por ejemplo, no devuelve `companyName`): si llega el id sin nombre se
    // resuelve, para no dejar el campo visualmente vacío pero con valor.
    if (this.prefilledContactId) {
      this.idContact = this.prefilledContactId;
      this.form.patchValue({ contactId: this.prefilledContactId });
      if (this.prefilledContactName) {
        this.contactSearchTerm = this.prefilledContactName;
      } else {
        this.resolveContactName(this.prefilledContactId);
      }
    }
    if (this.prefilledCompanyId) {
      this.idCompany = this.prefilledCompanyId;
      this.form.patchValue({ companyId: this.prefilledCompanyId });
      if (this.prefilledCompanyName) {
        this.companySearchTerm = this.prefilledCompanyName;
      } else {
        this.resolveCompanyName(this.prefilledCompanyId);
      }
    }
    if (this.prefilledDealId) {
      this.idDeal = this.prefilledDealId;
      this.form.patchValue({ dealId: this.prefilledDealId });
      if (this.prefilledDealName) {
        this.dealSearchTerm = this.prefilledDealName;
      } else {
        this.resolveDealName(this.prefilledDealId);
      }
    }
    if (this.prefilledDatetimeStart) {
      this.form.patchValue({ datetimeStart: this.prefilledDatetimeStart });
    }
    if (this.prefilledDatetimeEnd && this.prefilledDatetimeStart) {
      const startMs = new Date(this.prefilledDatetimeStart).getTime();
      const endMs = new Date(this.prefilledDatetimeEnd).getTime();
      const dur = Math.max(5, Math.round((endMs - startMs) / 60000));
      this.form.patchValue({ durationMinutes: dur });
    }

    // INC 39 — al abrir "Agendar reunión" desde un negocio/empresa/contacto solo
    // llega ESA entidad precargada; las demás quedaban vacías y la confirmación no
    // llegaba a nadie. Se dispara la misma cascada que al elegir el campo a mano
    // (los valores precargados no se pisan: cuentan como elección del usuario).
    if (this.prefilledDealId) {
      this.autofillFromDeal(this.prefilledDealId);
    } else if (this.prefilledCompanyId) {
      this.autofillFromCompany(this.prefilledCompanyId);
    } else if (this.prefilledContactId) {
      this.autofillFromContact(this.prefilledContactId);
    }
  }

  private initForm(): void {
    const now = new Date();
    // Redondear al próximo cuarto de hora
    const start = new Date(now);
    start.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);

    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(200)]],
      datetimeStart: [this.toDatetimeLocal(start), [Validators.required, this.noPastDate]],
      durationMinutes: [60, [Validators.required, Validators.min(5), Validators.max(1440)]],
      description: ['', Validators.maxLength(2000)],
      location: ['', Validators.maxLength(200)],
      meetingUrl: ['', [Validators.maxLength(500), Validators.pattern('https?://.+')]],
      // INC 17 — enlace de Google Maps (opcional; si tiene valor debe iniciar con http/https)
      locationMapUrl: ['', [Validators.maxLength(2048), Validators.pattern('https?://.+')]],
      idMeetingType: [null],
      idMeetingStatus: [null],
      dealId: [null],
      companyId: [null],
      contactId: [null],
      organizerId: [null],
      notes: ['', Validators.maxLength(2000)],
      outcome: ['', Validators.maxLength(2000)],

      // B13 — avanzado
      source: [null],
      videoProvider: [null],
      videoJoinUrl: ['', [Validators.maxLength(500)]],
      inviteeTimezone: [''],
      timezone: [''],
      isNoShow: [false],
      noShowDetectedAt: [null],
      paymentStatus: [null],
      paymentAmount: [null],
      paymentIntentId: [''],
      idCancellationReason: [null],
      cancellationReasonText: [''],
      gdprConsentGiven: [false],
      gdprConsentDatetime: [null],
    });

  }

  /**
   * Task 4 — autodetección de zona horaria SOLO en creación.
   * Precarga `timezone` (y `inviteeTimezone` si está vacío) con la zona del navegador,
   * sin pisar lo que el usuario ya eligió ni lo que vino de una booking page.
   * En edición no se toca (loadMeeting ya puso el timezone guardado).
   */
  private applyDetectedTimezone(): void {
    if (this.isEditing || !this.detectedTimezone) return;
    if (!this.timezones.includes(this.detectedTimezone)) return;

    const patch: Record<string, unknown> = {};
    if (!this.form.get('timezone')?.value) {
      patch['timezone'] = this.detectedTimezone;
      this.timezoneAutoDetected = true;
    }
    if (!this.form.get('inviteeTimezone')?.value) {
      patch['inviteeTimezone'] = this.detectedTimezone;
    }
    if (Object.keys(patch).length) {
      this.form.patchValue(patch);
    }
  }

  // ─── Fecha/hora helpers ────────────────────────────────────────────────

  // INC 9: solo en creación — editar una reunión histórica (notas, resultado) sigue permitido.
  // Tolerancia de 1 min para el botón "Reunión ahora".
  private noPastDate = (control: AbstractControl): ValidationErrors | null => {
    if (this.isEditing || !control.value) return null;
    const value = new Date(control.value);
    if (isNaN(value.getTime())) return null;
    return value.getTime() < Date.now() - 60000 ? { pastDate: true } : null;
  };

  get minDatetimeStart(): string {
    return this.toDatetimeLocal(new Date());
  }

  fieldLength(field: string): number {
    return (this.form.get(field)?.value || '').length;
  }

  private toDatetimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /** Setea la reunión a "ahora" (siguiente minuto exacto). */
  setStartNow(): void {
    const now = new Date();
    now.setSeconds(0, 0);
    this.form.patchValue({ datetimeStart: this.toDatetimeLocal(now) });
  }

  /** Aplica un preset de duración. */
  setDuration(minutes: number): void {
    this.form.patchValue({ durationMinutes: minutes });
  }

  /** El usuario cambió la zona horaria manualmente → ocultar el hint de autodetección. */
  onTimezoneManualChange(): void {
    this.timezoneAutoDetected = false;
  }

  /** INC 5: al elegir un tipo, la duración por defecto del tipo manda. */
  onMeetingTypeChange(): void {
    const typeId = +this.form.get('idMeetingType')?.value;
    const type = this.meetingTypes.find(t => t.id === typeId);
    if (type?.defaultDurationMinutes) {
      this.form.patchValue({ durationMinutes: type.defaultDurationMinutes });
    }
  }

  /** Devuelve la fecha/hora fin calculada para mostrar en UI. */
  get computedEndPreview(): string {
    const start = this.form?.get('datetimeStart')?.value;
    const dur = +this.form?.get('durationMinutes')?.value || 0;
    if (!start || !dur) return '';
    const end = new Date(start);
    if (isNaN(end.getTime())) return '';
    end.setMinutes(end.getMinutes() + dur);
    return end.toLocaleString();
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Reunión' : 'Nueva Reunión';
  }

  guardar(): void {
    this.submitAttempted = true;

    // Modalidad obligatoria: la reunión debe ser presencial (lugar) o virtual
    // (proveedor de video / enlace). Se comprueba ANTES del resto para poder
    // explicarle al usuario qué le falta en vez de fallar en el backend.
    const modalidadError = this.modalidadError;
    if (modalidadError) {
      this.form.markAllAsTouched();
      // El picker de canal de video vive en "Opciones avanzadas": abrirlo para
      // que el usuario vea la alternativa virtual sin buscarla.
      this.showAdvanced = true;
      // Resumen corto arriba (el detalle va junto a los campos implicados, sin
      // repetir el mismo texto) + scroll hasta el aviso, porque el botón Guardar
      // está al final del formulario.
      this.error =
        'Falta la modalidad de la reunión: revisa la sección “Ubicación (presencial)” o elige un canal de video en “Opciones avanzadas”.';
      this.modalidadErrorShown = true;
      setTimeout(() => {
        document
          .getElementById('meeting-modalidad-error')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const v = this.form.value;

    const isAutoProvider = this.isAutoVideoProvider(v.videoProvider);

    // Calcular datetimeEnd a partir de datetimeStart + durationMinutes.
    // INC 18: el datetime-local es hora local SIN offset; el backend interpreta
    // "sin offset" como UTC, así que hay que enviar ISO UTC real (con 'Z')
    // o la reunión queda desplazada (-5h en Lima) en cada guardado.
    const startDate = new Date(v.datetimeStart);
    const endDate = new Date(startDate.getTime() + (+v.durationMinutes || 60) * 60000);

    // INC 40 — modalidad excluyente (backend da 400 si location + meetingUrl juntos).
    // Presencial: NO hay canal de video → se envía location/locationMapUrl.
    // Virtual: hay canal de video. Automáticos (jitsi/google/microsoft) generan el
    // enlace en el backend; manuales (zoom/webex/other) mandan el enlace en meetingUrl
    // (campo único). videoJoinUrl ya no se envía (el backend lo espeja desde meetingUrl).
    const isPresencial = !v.videoProvider;
    const locationToSend = isPresencial ? (v.location || undefined) : undefined;
    const locationMapUrlToSend = isPresencial ? (v.locationMapUrl || undefined) : undefined;
    const meetingUrlToSend = isPresencial || isAutoProvider ? undefined : (v.meetingUrl || undefined);

    // INC 41 — IDs de participantes internos (excluyendo al organizador).
    const organizerId = v.organizerId ? +v.organizerId : undefined;
    const internalParticipantIds = this.internalParticipants
      .map((p) => p.userId)
      .filter((id) => id !== organizerId);

    const meetingRequest: MeetingRequest = {
      title: v.title,
      description: v.description || undefined,
      datetimeStart: startDate.toISOString(),
      datetimeEnd: endDate.toISOString(),
      location: locationToSend,
      meetingUrl: meetingUrlToSend,
      locationMapUrl: locationMapUrlToSend,
      idMeetingType: v.idMeetingType || undefined,
      idMeetingStatus: v.idMeetingStatus || undefined,
      dealId: v.dealId ? +v.dealId : undefined,
      companyId: v.companyId ? +v.companyId : undefined,
      contactId: v.contactId ? +v.contactId : undefined,
      organizerId: organizerId,
      businessId: businessId,
      notes: v.notes || undefined,
      outcome: v.outcome || undefined,

      // INC 41 — participantes internos (usuarios); el backend notifica.
      internalParticipantIds,

      // B13
      source: v.source || undefined,
      videoProvider: v.videoProvider || undefined,
      // INC 40 — videoJoinUrl ya no se envía; el enlace va en meetingUrl.
      inviteeTimezone: v.inviteeTimezone || undefined,
      timezone: v.timezone || undefined,
      isNoShow: v.isNoShow ?? undefined,
      noShowDetectedAt: v.noShowDetectedAt
        ? new Date(v.noShowDetectedAt).toISOString()
        : undefined,
      paymentStatus: v.paymentStatus || undefined,
      paymentAmount:
        v.paymentAmount !== null && v.paymentAmount !== ''
          ? +v.paymentAmount
          : undefined,
      // INC 43 — paymentIntentId (dato técnico interno) no se envía desde el form.
      idCancellationReason: v.idCancellationReason || undefined,
      cancellationReasonText: v.cancellationReasonText || undefined,
      gdprConsentGiven: v.gdprConsentGiven ?? undefined,
      gdprConsentDatetime: v.gdprConsentDatetime
        ? new Date(v.gdprConsentDatetime).toISOString()
        : undefined,
    };

    // INC 26 — prefill server-side desde una booking page (solo en creación)
    if (!this.isEditing && this.selectedBookingLinkId) {
      meetingRequest.idBookingLink = +this.selectedBookingLinkId;
    }

    if (this.isEditing && this.meetingId) {
      this.meetingApi.updateMeeting(this.meetingId, businessId, meetingRequest).subscribe({
        next: (meeting: MeetingResponse) => {
          this.saving = false;
          this.toast.updated('Reunión');
          this.handleAutoLinkResult(meetingRequest, meeting);
        },
        error: (err) => {
          this.saving = false;
          // INC 15: los errores de validación por campo vienen en `details`
          // ("campo: mensaje") → resaltar cada control afectado.
          const fieldMatched = this.applyBackendFieldErrors(err.error?.details);
          this.error = err.error?.details || err.error?.message || 'Error al actualizar la reunión';
          // Si todo el detalle mapeó a campos resaltados, no repetir el toast con el texto crudo;
          // mostrar el mensaje top-level (p.ej. validación de zona horaria) cuando lo haya.
          this.toast.error({
            title: 'Error',
            message: fieldMatched ? (err.error?.message || 'Revisa los campos marcados.') : this.error,
          });
        }
      });
    } else {
      this.meetingApi.createMeeting(meetingRequest).subscribe({
        next: (meeting: MeetingResponse) => {
          this.saving = false;
          this.toast.created('Reunión');
          this.handleAutoLinkResult(meetingRequest, meeting);
        },
        error: (err) => {
          this.saving = false;
          // INC 15: los errores de validación por campo vienen en `details`
          // ("campo: mensaje") → resaltar cada control afectado.
          const fieldMatched = this.applyBackendFieldErrors(err.error?.details);
          this.error = err.error?.details || err.error?.message || 'Error al crear la reunión';
          this.toast.error({
            title: 'Error',
            message: fieldMatched ? (err.error?.message || 'Revisa los campos marcados.') : this.error,
          });
        }
      });
    }
  }

  /**
   * Tras guardar (la reunión SIEMPRE quedó guardada: 200/201), decide qué
   * mostrar en el modal de resultado:
   *  - link generado por el back (google/microsoft/jitsi) → bloque copiable;
   *  - enlace de video NO generado → aviso accionable (videoSyncErrorCode);
   *  - correo de confirmación omitido → aviso (confirmationEmailSkipped).
   * Los avisos van en el modal (persistente) y NO como toast, para que no se
   * desvanezcan; cuando llegan los dos a la vez se listan uno debajo del otro
   * sin repetir el mismo texto. Si no hay nada que avisar ni link, se navega
   * o se emite `saved` directamente.
   */
  private handleAutoLinkResult(req: MeetingRequest, res: MeetingResponse): void {
    const wasAuto = this.isAutoVideoProvider(req.videoProvider);
    // Jitsi guarda la URL generada en meetingUrl; google/microsoft en videoJoinUrl.
    const generatedUrl =
      req.videoProvider === 'jitsi' ? res.meetingUrl : res.videoJoinUrl;

    this.postSaveWarnings = [];
    this.linkInfoUrl = null;
    this.linkInfoProvider = null;
    this.linkCopied = false;

    if (wasAuto && generatedUrl) {
      this.linkInfoUrl = generatedUrl;
      this.linkInfoProvider =
        req.videoProvider === 'google'
          ? 'Google Meet'
          : req.videoProvider === 'jitsi'
            ? 'Videollamada Jitsi'
            : 'Microsoft Teams';
    }

    if (wasAuto && !generatedUrl) {
      // Jitsi no depende de OAuth: si no vino link es un fallo del backend, no de la conexión.
      if (req.videoProvider === 'jitsi') {
        this.postSaveWarnings.push({
          title: 'Se guardó sin link de video',
          message: 'No se generó el enlace de la videollamada Jitsi. Reintenta guardando de nuevo.',
        });
      } else {
        // INC 71 — la reunión SÍ se guardó: es un AVISO, nunca un error rojo.
        // El mensaje es accionable según el código estructurado videoSyncErrorCode.
        this.postSaveWarnings.push({
          title: 'Se guardó sin enlace de video',
          message: this.videoSyncWarningMessage(res),
        });
      }
    }

    // Correo de confirmación omitido: el cliente NO recibió su confirmación.
    // Llega junto al fallo de enlace de video (misma causa) — son dos avisos
    // distintos y complementarios, no se pisan ni repiten el mismo texto.
    if (res.confirmationEmailSkipped === true) {
      this.postSaveWarnings.push({
        title: 'El correo de confirmación NO se envió al cliente',
        message:
          res.confirmationEmailSkippedReason ||
          'La reunión quedó sin enlace de videollamada y sin lugar presencial, por lo que no se envió la confirmación. Completa la modalidad y vuelve a guardar para que el cliente reciba el correo.',
      });
    }

    if (this.linkInfoUrl || this.postSaveWarnings.length) {
      this.postSaveMeeting = res;
      this.linkInfoVisible = true;
      return;
    }

    this.onSaveSuccess(res);
  }

  /**
   * INC 71 — construye un mensaje accionable en español a partir del código
   * estructurado del fallo de enlace de video. Si el backend envió un texto
   * legible (videoSyncError) se usa como base/fallback.
   */
  private videoSyncWarningMessage(res: MeetingResponse): string {
    switch (res.videoSyncErrorCode) {
      case 'ACCOUNT_NOT_CONNECTED':
        return 'La reunión se guardó, pero no se generó el enlace de video: conecta tu cuenta de Google en Configuración (Perfil) e inténtalo de nuevo.';
      case 'API_DISABLED_OR_FORBIDDEN':
        return 'La reunión se guardó, pero no se generó el enlace de video: la API de calendario no está habilitada o la cuenta conectada no tiene permisos.';
      case 'AUTH_EXPIRED':
        return 'La reunión se guardó, pero no se generó el enlace de video: tu conexión con Google expiró, vuelve a conectarla en tu Perfil.';
      case 'PROVIDER_ERROR':
        return res.videoSyncError ||
          'La reunión se guardó, pero el proveedor de video devolvió un error al generar el enlace. Inténtalo de nuevo más tarde.';
      case 'UNKNOWN':
      default:
        return res.videoSyncError ||
          'La reunión se guardó, pero no se pudo generar el enlace de video. Inténtalo de nuevo.';
    }
  }

  /** Modal post-creación con link generado. */
  copyLinkInfo(): void {
    if (!this.linkInfoUrl) return;
    try {
      navigator.clipboard?.writeText(this.linkInfoUrl);
      this.linkCopied = true;
      setTimeout(() => (this.linkCopied = false), 2500);
    } catch {
      // Ignorar — el usuario puede copiar manualmente
    }
  }

  closeLinkInfo(): void {
    this.linkInfoVisible = false;
    const meeting = this.postSaveMeeting;
    this.postSaveMeeting = null;
    this.linkInfoUrl = null;
    this.linkInfoProvider = null;
    this.linkCopied = false;
    this.postSaveWarnings = [];
    if (meeting) this.onSaveSuccess(meeting);
  }

  /** Título del modal post-guardado según haya o no avisos. */
  get postSaveTitle(): string {
    const accion = this.isEditing ? 'actualizada' : 'creada';
    return this.postSaveWarnings.length
      ? `Reunión ${accion} con avisos`
      : `Reunión ${accion}`;
  }

  private onSaveSuccess(meeting: MeetingResponse): void {
    if (this.drawerMode) {
      this.saved.emit(meeting);
    } else {
      this.router.navigate(['/meeting']);
    }
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/meeting']);
    }
  }

  toggleAdvanced(): void {
    this.showAdvanced = !this.showAdvanced;
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    // INC 15 — mensaje exacto que envió el backend para este campo
    if (control.errors['backend']) return control.errors['backend'];
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['pastDate']) return 'No se puede agendar una reunión en una fecha pasada';
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['pattern']) {
      if (field === 'locationMapUrl' || field === 'meetingUrl') {
        return 'Debe ser una URL válida que comience con http:// o https://';
      }
      return 'Formato inválido';
    }
    if (control.errors['min']) return `Mínimo ${control.errors['min'].min}`;
    if (control.errors['max']) return `Máximo ${control.errors['max'].max}`;
    return 'Campo inválido';
  }

  // ─── INC 15: resaltar el campo afectado por un error de validación del backend ──

  /**
   * Mapa de nombres del DTO del backend → nombres de control del form, cuando difieren.
   * Si el backend usa el mismo nombre que el control, no hace falta entrada aquí.
   */
  private readonly backendFieldMap: Record<string, string> = {
    meetingTypeId: 'idMeetingType',
    meetingStatusId: 'idMeetingStatus',
    cancellationReasonId: 'idCancellationReason',
  };

  /**
   * Parsea `err.error.details` (string con uno o varios "campo: mensaje" separados por
   * coma o salto de línea), marca cada control coincidente con `{ backend: mensaje }` y
   * lo toca para que se resalte. Devuelve true si al menos un campo fue resaltado.
   */
  private applyBackendFieldErrors(details: unknown): boolean {
    if (typeof details !== 'string' || !details.trim()) return false;

    // Separar por saltos de línea o por comas que preceden a "palabra:".
    const segments = details
      .split(/\r?\n|,(?=\s*[A-Za-z0-9_]+\s*:)/)
      .map((s) => s.trim())
      .filter(Boolean);

    let matchedAny = false;

    for (const segment of segments) {
      const idx = segment.indexOf(':');
      if (idx <= 0) continue;
      const rawField = segment.slice(0, idx).trim();
      const message = segment.slice(idx + 1).trim();
      if (!rawField || !message) continue;

      const controlName = this.backendFieldMap[rawField] || rawField;
      const control = this.form.get(controlName);
      if (!control) continue;

      control.setErrors({ ...(control.errors || {}), backend: message });
      control.markAsTouched();
      matchedAny = true;
    }

    return matchedAny;
  }

  /**
   * Suscribe cada control a `valueChanges` para borrar su error `backend` en cuanto el
   * usuario lo edita, de modo que el mensaje del backend no quede pegado tras corregir.
   */
  private setupBackendErrorClearing(): void {
    for (const controlName of Object.keys(this.form.controls)) {
      const control = this.form.get(controlName);
      if (!control) continue;
      control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (control.errors && control.errors['backend']) {
          const { backend, ...rest } = control.errors;
          control.setErrors(Object.keys(rest).length ? rest : null);
        }
        // El banner de modalidad desaparece en cuanto el usuario completa el
        // lugar presencial o elige un canal de video.
        if (this.modalidadErrorShown && !this.modalidadError) {
          this.error = '';
          this.modalidadErrorShown = false;
        }
      });
    }
  }
}